'use strict'

const MIN_DISTANCE = 500 // minimum distance between 2 locations to trigger an update
const LoaderType = {
  PRODUCTS: 'pdts',
  AVAILABILITIES: 'avails',
  INIT: 'init',
  AVAILS_NEXT: 'avails-next'
}

// list all possible errors.
// each value should be identical to the i18n key for the associated label.
const ErrorCodes = {
  AUTH_ERROR:                 'AUTH_ERROR',
  DISABLED_ERROR:             'DISABLED_ERROR',
  INVALID_TOUCHPOINT_ERROR:   'INVALID_TOUCHPOINT_ERROR',
  NO_EAN_ERROR:               'NO_EAN_ERROR',
  NOPRODUCT_ERROR:            'NOPRODUCT_ERROR',
  NOSTORE_ERROR:              'NOSTORE_ERROR',
  PRODUCT_CONFIG_ERROR:       'PRODUCT_CONFIG_ERROR',
  SERVER_ERROR:               'SERVER_ERROR',
  UNAVAILABLE_COUNTRY_ERROR:  'UNAVAILABLE_COUNTRY_ERROR',
  MISSING_REQUEST_LOCATION: 'MISSING_REQUEST_LOCATION',
}
// Error description, for internal use (Swaven & clients, not end-users)
const ErrorLabels = {
  AUTH_ERROR:                 'API authentication error',
  DISABLED_ERROR:             'Disabled widget (API response 402)',
  INVALID_TOUCHPOINT_ERROR:   'Touchpoint inconsistency between wtbConf and init params',
  NO_EAN_ERROR:               'Eans/Range not provided (DCO is not enabled)',
  NOPRODUCT_ERROR:            'API returned 0 products',
  NOSTORE_ERROR:              'API returned 0 availabilities',
  PRODUCT_CONFIG_ERROR:       'Provided EANS/range are invalid',
  SERVER_ERROR:               'Server error',
  UNAVAILABLE_COUNTRY_ERROR:  'Current country is not enabled',
  MISSING_REQUEST_LOCATION: 'Missing location from API request',  // this may happen when the location is unavailable for an alcohol account
}

// List values for the allowOos property. It's a bitfield, e.g. 3 means ALLOW + INIT.
const OosLevels = {
  NONE: 0, // never allowed
  ALLOW: 1, // allowed during wigdet use
  INIT: 2 // allowed at init
}

// src parameter for outbound_click click-command
const OUTBOUND_SRC = {
  BUY: 'buy',
  CLKLOC: 'clkloc',
  CUSTOM: 'custom',
  FALLBACK: 'fallback'
}

const EAN_MIN_LENGTH = 7,
      EAN_MAX_LENGTH = 14

// Mixins must be defined before app definition.
const i18nUpdateMixin = {
  methods: {
    // forces a re-render for app/component and all referenced child components.
    // That's because $forceUpdate() will only render app and components with slotted content.
    forceUpdate: function(){
      this.$forceUpdate()
      Object.values(this.$refs).forEach(cpnt => {

        // Sometimes cpnt is undefined. In LP legacy template, that can happen if the header is hidden, meaning the autocomplete component is unloaded or something.
        if (!cpnt)
          return

        if (typeof cpnt.forceUpdate == 'function')
          cpnt.forceUpdate()
        else if (typeof cpnt.$forceUpdate == 'function')
          cpnt.$forceUpdate()
      })
    },
  }
}

const addressMixin = {
  methods:{
    updateAutocomplete: function(e){
      // process only if the input bar has focus. Fixes IE11 bug where setting the value with :value="location.address" triggers an input event
      if (document.activeElement === e.currentTarget){
        const query = e.currentTarget.value
        this.$refs.autocomplete.request(query)
      }
    },
  }
}

const appDefinition = {
  data: function(){
    return {
      _location: {
        address: '',
        city: '',
        zipCode: '',
        region: '',
        country: '',
        latitude: 0,
        longitude: 0,
        source: '',
        isEmpty: true,
        boundingBox: null,
        auto: null,
        type: '',
        isDefault: false, // whether location is the API default location
      },
      legacyCustomStyle: {},
      legacyCustomStyleValues: {},
      customStyleMapping: {},
      filteredProducts: [],
      products: [],
      productTotalCount: null, // total number of results returned by paginated request.
      // default empty product. Define properties with empty values, to avoid "undefined" strings.
      product: {
        name:'',
        name2:'',
        pkg:'',
        description:'',
        ratings: {}
      },
      // map of master products and their variants. Populated when masterMode is enabled.
      productGroups: null,
      availabilities: [], // all availabilities returned by API
      onlineAvailabilities: [], // filtered availabilities, online stores only
      onlineAndDriveAvailabilities: [], // filtered availabilities, online stores + offline ones with clkurl
      realAvailabilities: [], // filtered availabilities, real stores only
      byTypologyAvailabilities: [], // filtered availabilities, one result  per typology
      filteredAvailabilities: { // filtered abilities
        all: [],
        online: [],
        real: [],
        byTypology: []
      },
      offlineGroups: null, // map[store id] -> [availabilities] for offline multi-offer
      availability: null, // selected availability
      allEans: null, // object with countries as keys and array of eans/range as values
      idType: null, // type of id used for product selection. Directly passed to API calls.
      wtbid: null,
      appId: null,
      locale: null,
      test: false, // whether the experience is a test configuration
      baseLang: null,
      sizeMode: 'adjust',
      fixedIframeHeight: false, // keep temporarily while we still have templates on the old mode
      theme: null,
      templateLayout: '', // OPS-4044 (default popin/drawer/insite widget),  but option may be used by any other template.
      wtbConf: {},
      i18n: null,
      defaultEan: null,
      availPrdOnly: null,
      showWarning: true, // set to false when closing the test/nocache warning
      alterUrl: null, // in media, url to redirect to when clicking the widget background
      fallbackStoreId: null, // id of the default store whose clickUrl to use when clicking media widget background without an alterUrl defined
      initDone: false, // whether init method has run
      defaultCountry: null,
      countries: [], // list of countries (ISO 3166 alpha 2) the widget support. Intersection of configured countries (from config) and context countries (from tag)
      filters: [], // filters definition
      productFilters: {}, // products filters definition
      isMedia: false, // whether the widget is a media one (e.g. banner, video)
      hideHostScroll: false, // whether the host scroll is disabled for popin widgets
      isPopin: null, // whether the widget is a popin
      showProductFilters: null, // whether we need to show filters on products or not
      catalogSelector: false, // wether template uses catalog selector (no EAN mode)
      productSearchEnabled: false, // whether a product search bar renders in the catalog selector
      catalogFilters: null, // serialized filters for filters selection
      useProductPagination: false, // whether to switch products API endpoint to use paginated search.
      mapVisible: false,
      availRequestDone: false, // whether the first availability request has completed. Used in templates for error handling
      noCache: false,
      layoutName: '', // if init params include a layout name, store it here for future reference
      withSafeFrame: false,
      pdtFilters: [], // product filters definition
      rtl: false, // false by default, i18n is responsible of it's value when setting the lang
      presetPdtFilters: '', // product preset, coming from host url
      displayPrices: false, // whether wtbConf allow displaying prices
      lockdown: false, // global lockdown flag
      lockdownNotifClosed: false,
      geolocProvided: false, // whether geoloc parameter was provided to widget
      firstOpeningDone: false,
      isOpen: false, // open/close status of the widget
      allowOos: 0, // whether out-of-scope is allowed
      Utils: Utils, // So we can use utility methods in templates
      onlineOnly: false, // true to show only online stores
      offlineOnly: false, // true to show only offline stores
      renderContext: undefined,
      draft: false, // whether conf comes from draft (from studio or preview)
      delayGeoloc: false, // whether to prevent geolocation request on load
      availSortOrder: null, // when sorting/merging avails list, order is defined by conf.
      retailerPriorities: null, // ordered list of retailer ids
      pinnedRetailers: null, // ordered list of pinned retailer ids
      dnt: false, // Do Not Track mode (MME-38)
      openFacets: [], // filter facets to open by default
      isStoreLocator: false,
      maxDistance: 0,
      // masterProduct mode: dedup products to show masters only
      // multiple-products API avail calls
      masterProductMode: false,
      // multiOffer: how the widget handles multiple avails for a given
      // onine retailer or physical store.
      // false: dedups are fitlered to show only one avail per retailer/store
      // true: (only when template supports it) avails are also deduped, but:
      // - online: show 1 only
      // - physical: show nested under store details
      multiOffer: false,
      // enabled class of trade values for VIP filtering
      classOfTradeFilters: null,

      availsFacets: null, // dictionary<string, string[]> for different facets. Initialized when needed

      // stores current error code.
      // Currently used only for unavailable country error. If used for other errors, one must not forget to reset it when the error is no longer relevant.
      // The aim of this is to show the "no store available" message only when needed, not when store list is empty due to other errors.
      error: '',

      // custom reactive properties can only be added to a reactive object, not to vue root.
      // this object is there to hold custom  properties that are specific to a template.
      customProps: {},

      //  Distinct retailers threshold below which not to display any non-DTC retailers
      tiedHouseThreshold: 0,

    // US alcohol settings: some states require retailers to show as text instead of logos.
    showRetailerText: false,
    showLocalRetailers: null, // (nullable boolean) US alcohol settings: whether local results are returned

    // US alcohol settings: custom texts when no offline or filtered results
    alcoholCustomTexts:{
      noOffline: '',
      hiddenStores: '',
    },

      // NOTE: properties starting with an underscore are not accessible directly on the instance, but through the $data object.
      _rafid: null, // requestAnimationFrame id for height adjustment
      _currentHeight: 0,
      _trkData: {},
      _iframeSize: {width: -1, height: -1},
      _templateSelectionResults: {},
      _pendingProductReq: '', // url of the currently running product request
      _lastProductReq: '', // url of the last successful, non-empty product request
      _availAborter: null, // AbortController for availability requests
      _pdtAborter: null, // AbortController for product requests
      _intersectObserver: null, // IntersectionObserver instance
      _trackedAvails: new Set(), // all availabilities that have been visible since last check
      _availTrackItv: null, //interval id for function that tracks visible stores
      _resizeTimout: null, //timeout id for window resize debouncing
      _widgetObserver: null, // observer to track widget visibility. cleared after first call
      _backgroundVideo: null, // url of the video to play in background
      _refParams: null, // studio only: params object passed to the first init call
    }
},
  watch: {
    product: function(pdt, old){
      this.getAvailabilities()
      this.trigger('product-select', {pdt: pdt})
    },
    products: function(){
      this.filteredProducts = this.products
    },
    location: function(loc, old){
      this.location.isEmpty = false
    },
    filteredProducts: function(products){
      // if current product is not in the filtered list, reset product to defaut ean or first of list
      if (products.length && products.length !== this.products.length && !!this.product.id && !products.find(x => this.product.id === x.id)){
        let newPdt
        if (this.defaultEan && this.defaultEan[this.country])
          newPdt = this.products.find(pdt => Utils.isDefaultProduct(pdt, this.defaultEan[this.country], this.idType))
        this.selectProduct(newPdt || products[0])
      }
    },
    // creates filtered lists of availabilities:
    // onlineAvailabilities: online stores + drive/collect (deduped by typology)
    //  + alias list onlineAndDriveAvailabilities
    // realAvailabilities: offline stores
    // byTypologyAvailabilities: all avails, deduped by typology
    //
    // + their filtered equivalents
    availabilities: function(avails){

      // in multi-offer only: map of dedup-key -> [avails]
      const offlineGroups = new Map()

      // returns the different lists.
      // NOTE: offlineGroup map object is updated in place by sortAvailabilities.
      let {online, offline, typologies } = Utils.sortAvailabilities(avails, this.multiOffer,  offlineGroups, this.availSortOrder, this.retailerPriorities, this.pinnedRetailers)

      // if class-of-trade filtering enabled: remove non-VIP results from offline list
      if (this.availsFacets?.cots?.length > 0) {
        offline = offline.filter(avail => avail.retailer.id === 3364 )
      }

      //  Now that all the splitting and sub-filtering is done,
      //  apply the tied house filter again,
      //  since individual tab views of these availabilities must comply with tied-house law
      this.realAvailabilities = this.filteredAvailabilities.real = this.filterForTiedHouseLaw(offline)
      this.byTypologyAvailabilities = this.filteredAvailabilities.byTypology = this.filterForTiedHouseLaw(typologies)
      this.onlineAvailabilities = this.filteredAvailabilities.online = this.onlineAndDriveAvailabilities = this.filterForTiedHouseLaw(online)
      this.filteredAvailabilities.all = avails

      if (this.multiOffer)
        this.offlineGroups = offlineGroups

      // if current selection is not in the new stores, reset selected availability.
      if (this.availability && avails.find(s => s.store.id === this.availability.store.id) == null){
        this.availability = null
        if (this.$refs.map)
          this.$refs.map.clearSelection()
        this.trigger('availability-select', {avail: null, area: Area.MAIN})
      }

      // wait for the DOM update to launch the store visibility tracking
      this.$nextTick().then(() => {
        Tracker.log('avails-update', {count: this.availabilities.length})
        if (this.availabilities.length)
          this.trackVisibleStores()
      })
    },
    rtl: function(rtl){
      // if selected locale is RTL, the loader must be told to configure embedded buttons appropriately
      if (rtl)
        hostCom.send('awe:setRtl')
    },
    catalogFilters: function(){
      this.getProducts(false, false, true)
    }
  },
  computed: {
    // eans for the current country
    eans: function(){
      if (!this.allEans || !this.country || !this.allEans[this.country])
        return []
      return this.allEans[this.country]
    },
    // current country
    country: function(){
      if (this.location?.country && this.countries.includes(this.location.country))
        return this.location.country
      else
        return this.defaultCountry
    },
    // whether the eans array contains a range
    isRange: function(){
      return this.eans.length === 1 && this.eans[0].length === 24
    },
    location: {
      get: function(){
        return this.$data._location
      },
      set: function(loc){
        if (loc.auto == null || loc.auto === '')
          console.log('no auto param on geoloc !')

        if (this.countries.indexOf(loc.country) > -1){
          // location country is in scope: refresh product if country changed or if availability checks are required.
          // Otherwise the product list does not change, so just refresh availabilities UNLESS loc update comes from availability API.
          // In that case no need to re-run request that will give the same results.

          // console.debug(`OK: ${loc.country} in scope`)

          let distance = 0,
              refreshProducts = false

          // check distance & wether to refresh products only when necessary
          if (!this.location.isEmpty){ // whether previous location is the initial empty one
            distance = Utils.getHaversineDistance(loc, this.location)
            refreshProducts = (loc.country != this.location.country || this.availPrdOnly) && loc.source !== 'api-avail'
          }
          // if widget is currently out of scope, force refresh products now that a new location is in scope.
          else if ([ErrorCodes.UNAVAILABLE_COUNTRY_ERROR,
                    ErrorCodes.MISSING_REQUEST_LOCATION].includes(this.error)){
            distance = MIN_DISTANCE + 1
            refreshProducts = true
          }

          this.$data._location = loc

          if ([ErrorCodes.UNAVAILABLE_COUNTRY_ERROR,
               ErrorCodes.MISSING_REQUEST_LOCATION].includes(this.error))
            this.error = ''

          // refresh products/avails only if new location far enough from the previous one
          if (distance > MIN_DISTANCE){
            if (refreshProducts)
              this.getProducts(this.eans)
            else if (loc.source !== 'api-avail')
              this.getAvailabilities()
          }

          // save location to session storage, but NOT when location comes from API (API-3099)
          if (!loc.source.startsWith('api-'))
            hostCom.send('awe:saveLocation', {loc: loc})

          this.trigger('location-update', {location: loc})
        }
        // automatic geoloc is out of scope: ignore it.
        else if (loc.auto || loc.auto == null && loc.source != 'manual'){}
        else{
          // location's country is out of scope: ignore it.
          console.warn(`'${loc.country}' is out of scope.`)

          // OOS  allowed
          if ((this.allowOos & OosLevels.ALLOW) === OosLevels.ALLOW){
            this.$data._location = loc
            this.availabilities = []
            this.trigger('location-update', {location: loc})
            this.trigger('availabilities-update', {avails: []})
            hostCom.send('awe:saveLocation', {loc: loc})
          }
          this.raiseError(ErrorCodes.UNAVAILABLE_COUNTRY_ERROR)
        }
      }
    },
    langs: function(){
      if (this.locale === this.baseLang)
        return this.locale
      else
        return this.locale + ' ' + this.baseLang
    },
    // duplicate productTotalCount under old name, to ensure compatibility with template until its related update.
    productTotal: function(){
      return this.productTotalCount
    }
  },
  created: function(){
    window.i18n = this.i18n = Vue.markRaw(new I18n(this))
    // first opening only: wait for init to be completed, then run some checks
    hostCom.on('awe:open', e => {
      this.isOpen = true

      if (this.initDone)
        return this.open(e.params)

      let itv = setInterval(() => {
        if (this.initDone){
          clearInterval(itv)
          this.open(e.params)
        }
      }, 50)
    })

    hostCom.on('awe:close', e => { this.isOpen = false })
  },

  updated: async function(){
    // monitor height only when enabled and ResizeObserver is not available.
    // If it exists, app-ctnr is monitored, and manual check is not needed.
    if (this.sizeMode === 'adjust' && !window.ResizeObserver)
      this.checkHeight()
  },
  directives: {
    // when set on a node, forces map resize when the node is updated.
    // delay call to function to make sure the rendering is done so the resize is done correctly.
    'resize-map': {
      componentUpdated: function(){
        setTimeout(app.$refs.map.resize, 50)
      }
    }
  },
  mixins: [i18nUpdateMixin, addressMixin],
  methods: {
    init: async function(params, wtbConf){
      performance.measure('appinit', 'app-start')

      this.wtbConf = wtbConf
      this.locale = this.i18n.setLang(params.locales)
      this.baseLang = this.i18n.baseLang
      this.dnt = params.dnt

      if (wtbConf.glassbox) {
        if (params.glassbox)
          // found at load: inject. Object is passed at init only if glassbox is recording, no need to check for that again.
          this.injectGlassbox(params.glassbox.sessionId, params.glassbox.reportURI, wtbConf.glassbox)
        else {
          // ...or request loader to retrieve details
          hostCom.send('awe:glassbox', data => {
            if (data.enabled)
              this.injectGlassbox(data.sessionId, data.reportURI, wtbConf.glassbox)
          })
        }
      }

      // inject style defined in the remote conf
      if (this.wtbConf.enableremoteconf && this.wtbConf.widgetclientparams)
        this.injectCustomStyle(this.wtbConf.widgetclientparams.params)

      // if opener button is embedded (custom) and selected locale different than first value in list, notify loader to update the button's class
      if (params.embeddedBtn && this.locale !== params.locales[0])
        this.updateLocale(this.locale, params.locales[0], this.i18n.baseLang, params.locales[0] && params.locales[0].split('-')[0])

      // if iframe title is specified for a locale and selected locale different than first value in list, notify loader to update the iframe title
      if (this.locale !== params.locales[0] &&  params.localesConf[this.locale]?.iframeTitle)
        hostCom.send('awe:updateTitle', {newTitle: params.localesConf[this.locale].iframeTitle})

      const ariaLabel = (params.localesConf?.[this.locale]?.iframeTitle) || params.title
      this.$el.setAttribute('aria-label', ariaLabel)

      if (params.wtbid)
        this.wtbid = params.wtbid
      if (params.appid || params.appId)
        this.appId = params.appid || params.appId
      if (params.templateLayout)
        this.templateLayout = params.templateLayout
      if (params.productSearchEnabled)
        this.productSearchEnabled = true
      if (params.catalogSelector)
        this.catalogSelector = true
      else if (params.eans){
        if (this.allEans == null)
          this.allEans = params.eans
        else
          // if object is not null, that means it has been set by an update from an opener button before the init function has run.
          // in that case, user wants to see the product he has selected, not the default products for the widget.
          // So we update the object instead of overwriting it.
          this.allEans = Object.assign({}, params.eans, this.allEans)
      }
      if (params.defaultEan)
        this.defaultEan = params.defaultEan

      this.idType = params.idType
      this.masterProductMode = !!params.masterProductMode
      this.multiOffer = !!params.multiOffer
      this.theme = params.theme || ''
      this.availPrdOnly = !!params.availPrdOnly
      this.addNearestOnDelivery = !!params.addNearestOnDelivery

      this.isPopin = !params.containerId

      // widget dimensions provided: apply them immediately
      if (params.width && params.height){
        this.sizeMode = 'fixed'
        this.setRootSize({width: params.width, height: params.height})
      }
      else if (params.freeSize || this.isPopin)
        this.sizeMode = 'free'
      else
        this.monitorHeight()

      // keep compatibility with older templates that use it (lp-legacy)
      this.fixedIframeHeight = this.sizeMode !== 'adjust'

      this.showProductFilters = !!params.showProductFilters
      this.defaultCountry = params.defaultCountry
      this.countries = params.enabledCountries
      this.displayPrices = !!wtbConf.displayprices || !!(wtbConf.enableremoteconf && wtbConf.widgetclientparams?.params?.['show-prices'])
      this.onlineOnly = !!params.onlineOnly
      this.offlineOnly = !!params.offlineOnly
      this.isStoreLocator = wtbConf.isStoreLocator
      this.maxDistance = wtbConf.enableremoteconf && wtbConf.widgetclientparams?.params?.['max-distance'] || 0
      this.classOfTradeFilters = wtbConf.enableremoteconf && wtbConf.widgetclientparams?.params?.['class-of-trade']

      if (this.classOfTradeFilters != null) {
        this.availsFacets = this.availsFacets || {}
        this.availsFacets.cots = []
      }

      this.test = wtbConf.test || params.test || /test=(1|true)/i.test(params.referrer2)
      this.openFacets = params.openFacets || []

      this.renderContext = params.renderContext || undefined
      this.draft = !!params.draft
      this.delayGeoloc = !!params.delayGeolocation

      if (this.renderContext === 'studio'){
        if (!this.$data._refParams)
          this.$data._refParams = params

        // remote conf update listener is only plugged when in studio mode
        if (!hostCom.hasKey('awe:style-update')){
          hostCom.on('awe:style-update', e => {
            if (this.$data._refParams){

              this.init(
                // locale from remote conf is taken at loader sanitizeParams.
                // So we need to overwrite this parameter now if it's been updated.
                {...this.$data._refParams,
                  locales: [this.wtbConf.widgetclientparams.params.locale],

                  // theme is taken from init params and not from the conf, so it must be added in this object.
                  theme: e.params.theme,
                },
                {...this.wtbConf,
                  widgetclientparams: {params: e.params}
                })
            }
          })
        }
      }

      this.noCache = /nocache=(1|true)/i.test(params.referrer2) || this.draft || !!this.renderContext
      // disable warnings in studio/preview as we use non-cached endpoints.
      if (this.draft || !!this.renderContext)
        this.showWarning = false

      // initial location setting must not trigger setter logic as that would trigger a getProducts request, not wanted at this time;
      // so we directly set the underlying private property.
      if (params.geoloc){
        this.geolocProvided = true
        if (!params.geoloc.zipCode || !params.geoloc.address || !params.geoloc.country || !params.geoloc.city){ // geoloc is missing some data, reverse geoloc from lat/lng
          const country = params.geoloc.country ? [params.geoloc.country] : this.countries,
                loc = await Utils.getReverseGeocoding(params.geoloc.longitude, params.geoloc.latitude, country, this.locale, 'init')
          if (loc)
            this.$data._location = loc
          else if (params.location){
            this.$data._location = params.location // fallback to persisted location if there's an error with reverse geoloc
            this.geolocProvided = false // reverse geoloc failure, consider it not provided
          }
        }
        else // geoloc provides everything, we can just assign it.
          this.$data._location = params.geoloc
      }
      else if (params.location) // if there's a persisted location but no geoloc, use that one
        this.$data._location = params.location

      if (params.media != null)
        this.isMedia = !!params.media

      // inconsistency between shoptag-provided value and conf value cannot be allowed as tracking will be a mess:
      // Stop everything and cry about it.
      if (params.touchpoint && params.touchpoint !== this.wtbConf.touchPoint){
        this.raiseError(ErrorCodes.INVALID_TOUCHPOINT_ERROR)
        return
      }

      if (params.layout)
        this.layoutName = params.layout

      this.withSafeFrame = !!params.withSafeFrame
      this.tracker = Vue.markRaw(new Tracker(params, this.wtbConf, params.perf.loader + params.perf.switch))

      // Investigate API-3010
      if (wtbConf.geoCountry == null){
        console.log('geocountry is null')
        Tracker.log('geocountry_null', {})
      }
      if (app.country == null){
        console.log('app.country is null')
        Tracker.log('country_null', {defaultCountry: this.defaultCountry, locCountry: this.location.country})
      }

      // if widget is a popin, bind keyup event to close widget if escape key is pressed.
      // Check if event target is not input, in which case it's the address input field and escape key
      // is to close the autocomplete list, not to close the popin.
      if (this.isPopin){
        document.body.addEventListener('keyup', e => {
          if (e.key === 'Escape' && e.target.nodeName !== 'INPUT')
            this.closeWidget(e)
        })
      }

      /*
      // look for background image in remoteConf.
      if (this.isMedia && !params.backgroundImg && this.wtbConf.enableremoteconf && this.wtbConf.widgetclientparams){
        const key = `layout-${params.width}x${params.height}-bg-img`
        const img = this.wtbConf.widgetclientparams.params[key]
        if (img)
          params.backgroundImg = img
      }
      if (params.backgroundImg)
        this.setBackgroundImage(params.backgroundImg, params.referrer2)
      */

      // if map exists and has startVisible attribute set or modeStoreLocator is true, show map immediately
      if (this.$refs.map && (this.$refs.map.startVisible || params.modeStoreLocator))
        this.showMap(null, false, true)

      // alterUrl is enabled if provided and explicitely enabled
      this.alterUrl = params.alterUrlEnabled && params.alterUrl

      /*
      // API-2917: in banner, all clicks must trigger a redirection. Listen to click events on the root element as a fallback to catch all clicks.
      // disableBackgroundClick allows bypassing this behavior
      if (this.isMedia && !params.disableBackgroundClick)
        this.$el.addEventListener('click', this.redirectBackgroundClick)
      */

      this.hideHostScroll = !!params.hideHostScroll
      this.pdtFilters = Mapping.mapPdtFilters(wtbConf.prdFilters)
      this.presetPdtFilters = params.presetPdtFilters
      this.allowOos = params.allowOos
      this.useProductPagination = !!params.productPagination

      // if config allows out-of-scope at init, also allow OOS-geoloc.
      if (this.allowOos > OosLevels.ALLOW)
        this.allowOos = this.allowOos | OosLevels.ALLOW

      this.availSortOrder = Symbol.for(wtbConf.retcfg && wtbConf.retcfg.onlineStoresOrder || 0)
      if (this.availSortOrder === AVAIL_SORT_ORDER.DEFAULT && wtbConf.retcfg?.priorities?.length){
        this.availSortOrder = AVAIL_SORT_ORDER.PRIO_LIST
        this.retailerPriorities = wtbConf.retcfg.priorities
      }

      if(wtbConf.retcfg?.pinnedRetailerIds?.length) {
        this.pinnedRetailers = wtbConf.retcfg.pinnedRetailerIds
      }

      if (wtbConf.operator.alcoholCustomTexts){
        this.alcoholCustomTexts = {
          noOffline: wtbConf.operator.alcoholCustomTexts.noOfflineStoreText || '',
          hiddenStores: wtbConf.operator.alcoholCustomTexts.hiddenStoresText || '',
        }
      }

      this.initDone = true

      if (!this.isMedia && window.IntersectionObserver){
        this.$data._widgetObserver = new IntersectionObserver(([entry], obs) => {
          if (entry.isIntersecting && entry.intersectionRect.height >= 30){
            // console.debug(`Widget visible ${entry.intersectionRect.height}px [${entry.intersectionRatio}]`)
            Tracker.dropTag('visible')
            obs.disconnect()
            this.$data._widgetObserver = null
          }
          // else
          //   console.debug(`Not enough intersection: ${entry.intersectionRect.height}/${entry.boundingClientRect.height}px [${entry.intersectionRatio}]`)
        }, { root: null, rootMargin: '0px', threshold: [.01, .02, .04, .06, .08, .1, .12, .15, .2, .4] })
      }

      // normalizing wtbConf locale-based params
      this.wtbConf.widgetclientparams.params = Utils.normalizeLocaleKeys(this.wtbConf.widgetclientparams.params)

      // send params to a template script (if there is one) for updating i18n text or custom properties
      this.trigger('app-init', {locale: this.locale, params: params, wtbConf: this.wtbConf})


      const appInit = performance.getEntriesByName('appinit', 'measure')[0].duration,
            loadTime = params.perf.loader + params.perf.switch + appInit

      const initTrk = {
        loadTime: loadTime.toFixed(0),
        cpus: navigator.hardwareConcurrency,
        intersectionObs: +!!window.IntersectionObserver,
        perf_appInit: appInit.toFixed(0)
      }
      if (navigator.deviceMemory != null)
        initTrk.memory = navigator.deviceMemory
      Object.entries(params.perf).forEach(([k,v]) => {
        if (v)
          initTrk[`perf_${k}`] = v.toFixed(0)
      })

      console.debug(Object.entries(initTrk).map(([k,v]) => `${k}:${v}${k.startsWith('perf_') ? 'ms':''}`).join('   ') )
      Tracker.log('initMetrics', initTrk)
    },

    open: function(data){

      let eansUpdate = false, // whether current eans are updated
          defaultEanUpdate = false // whether default ean for current country is updated

      // keep reference to current product before assigning new scope, as needed for determining if a loader is needed.
      const currentProduct = this.product

      // id type may be unknown at init time (default gtin) and need to be set on opening.
      if (data.idType)
        this.idType = data.idType

      this.tracker.openerIdx = data.openerIdx

      if (data.theme)
        this.theme = data.theme

      if (data.eans){
        const oldEans = this.eans
        this.allEans = Object.assign({}, this.allEans, data.eans)
        eansUpdate = !Utils.arraysEqual(oldEans, this.eans) // checks whether eans have changed
      }

      if (data.trackdploy){
        Tracker.dropTag('dploy', data.trackdploy)
      }

      if (data.defaultEan){
        const oldDefault = this.defaultEan && this.defaultEan[this.country],
              isCurrentDefault = Utils.isDefaultProduct(this.product, this.defaultEan?.[this.country], this.idType)

        this.defaultEan = Object.assign({}, this.defaultEan, data.defaultEan)

        // update if current default is different from the old one
        defaultEanUpdate = oldDefault != this.defaultEan[this.country]
      }

      if (!this.countries.includes(this.country)){
        this.raiseError(ErrorCodes.UNAVAILABLE_COUNTRY_ERROR)
        this.products = []
        this.availabilities = []

        if (this.location.isEmpty)
          this.setDefaultAddress()

        return
      }

      // Checks that eans are defined, unless DCO is enabled
      const dcoEnabled = this.isMedia && this.wtbConf.isDco

      if (this.eans.length === 0 && !dcoEnabled && !this.catalogSelector && !this.useProductPagination){
        this.raiseError(ErrorCodes.NO_EAN_ERROR)
        console.warn(`Missing range/eans for country ${this.country}.`)
        this.products = []
        this.availabilities = []
        return
      }
      else if (eansUpdate)
        // clear only if a product request will be done. Otherwise a server/auth error should still be displayed.
        this.$refs.errMon.clear()

      // if eans are defined, check they're valid. If so, clear eventual invalid eans error. Otherwise show error and return
      if (this.eans.length > 0){
        const fn = !!this.idType
          // no constraint on id if type is provided
          ? x => !!x
          // whether ean list contain at least 1 valid ean or is a range
          : x => x.length >= EAN_MIN_LENGTH && x.length <= EAN_MAX_LENGTH || this.isRange
        const isValid = this.eans.some(fn)

        if (!isValid){
          this.raiseError(ErrorCodes.PRODUCT_CONFIG_ERROR)
          console.warn(`Missing or invalid range/eans: '${this.eans}' for country ${this.country}.`)
          this.products = []
          this.availabilities = []
          return
        }
        else if (eansUpdate)
          // clear only if a product request will be done. Otherwise a server/auth error should still be displayed.
          this.$refs.errMon.clear()
      }

      // on first opening or when eans were updated: fetch products
      if (!this.firstOpeningDone || eansUpdate){
        // If default ean has been updated, parameter ignoreCurrentSelection is true,
        // ignoring current product selection and using the default ean instead.
        this.getProducts(this.eans, false, defaultEanUpdate)
      }
      else if (data.defaultEan){
        // if default ean is present on button, look for ean in current products and
        // select it if found.
        const prd = this.products.find(pdt => Utils.isDefaultProduct(pdt, this.defaultEan?.[this.country], this.idType))
        if (prd){
          this.selectProduct(prd, true)
          if (this.$refs.slider)
            this.$refs.slider.init()
        }
      }

      if (!this.firstOpeningDone){
        // Do not get user location if the current one has been set manually OR if this is a media widget OR default geoloc was provided at init.
        // In media, we do not want to trigger browser geoloc api (don't bother user asking permission for a banner),
        // and products endpoint (location fallback) does not return location when using caching API.
        if (this.location.source !== 'manual' && !this.isMedia && !this.geolocProvided && !this.delayGeoloc)
          this.autoloc()
      }

      // trigger event for an eventual template listener.
      // Only if widget is actually opened by the call (not triggered if widget is already opened)
      if (!data.isOpen)
        this.trigger('widget-open')

      if (eansUpdate){
        // data.isOpen is in the context of the loader, not the widget. So if it's false, the widget is opening.
        // this.isOpen is set to true before calling the open method, so will always be true no matter what.
        this.trigger('scope-update', {eans: this.eans, opening: !data.isOpen})

        // re-opening the widget  with a different eans scope, show a loading spinner until image is loaded.
        if (this.firstOpeningDone) {

          // checks if current product is in the new scope
          const inNewScope = this.eans.some(ean => Utils.isDefaultProduct(currentProduct, ean, this.idType))

          // show loader if:
          // - the new scope does not include the current product OR
          // - current product is in scope but default ean specifies another product.
          // In other cases, the selected product does not change and does not need a loader.
          if (!inNewScope
            || this.defaultEan?.[this.country]
            && !Utils.isDefaultProduct(currentProduct, this.defaultEan?.[this.country], this.idType)) {

            this.showImageLoader()
          }
        }
      }



      if (this.hideHostScroll && this.isPopin)
        hostCom.send('awe:hideScroll')

      this.firstOpeningDone = true

      if (this.$data._widgetObserver)
      // check again for observer when executing the function, since the observer may be
      // cleared if it runs during the timeout delay.
        setTimeout(() => { this.$data._widgetObserver && this.$data._widgetObserver.observe(this.$el) }, 400)

      if (this.isPopin){
        // set focus on first focusable element
        setTimeout(() => {
          // selectors, ordered by priority
          const selectors = ['.product-slider', '.product-dropdown', 'input[type="text"]', '#close', '.carousel', 'input']
          let focusNd, i = 0

          do{
            focusNd = document.querySelector(selectors[i])
          } while (++i < selectors.length && !focusNd)

          if (focusNd)
            focusNd.focus()
        }, 400)
      }
    },

    // fetch products from the server
    // pids: array of product ids to fetch
    // forceLoc: whether to force location update from api
    // ignoreCurrentSelection: whether to ignore current product selection and use the default ean instead.
    // defaultEanUpdate: whether the default ean has been updated. If so, the product selection is updated.
    getProducts: async function(pids, forceLoc, ignoreCurrentSelection, defaultEanUpdate = false){
      if (this.useProductPagination){
        return this.getPaginatedProducts(0, null, forceLoc, ignoreCurrentSelection, 0)
      }
      else{
        const ok = await this._getProducts(pids, forceLoc)

        // only update selected product if either:
        //  - the request was successful
        //  - the default ean was changed
        if (ok || defaultEanUpdate){
          this.updateProductSelection(ignoreCurrentSelection)
          this.trigger('products-update', {products: Utils.clone(this.products), defaultId: this.product.id})
        }
      }
    },

    // return true if the request was successfully sent and the product list was updated.
    _getProducts: async function(pids, forceLoc = false){
      if (
        // pids must be provided and not be bogus.
        // Tag uses a fake ean '00000...' to trigger the widget load when there's no product context (e.g. catalog mode).
        // No need to request anything in that case, facets request provides an ean.
        (!pids || pids.length === 0 || pids.length === 1 && /^0+$/.test(pids[0]))
        && !this.isMedia
        && !this.useProductPagination
        )
        return false

      if (!this.countries.includes(this.country))
        return false

      let url

      try{
        url = Utils.buildProductsRequestUrl(this.wtbid, this.locale, this.country, this.availPrdOnly, pids, this.location, forceLoc, this.draft, this.idType, null, null, null, this.tracker)

        // check if running request has the same url. If so, no need to duplicate it.
        // Otherwise keep track of the url
        if (url === this.$data._pendingProductReq || url === this.$data._lastProductReq)
          return false
        else
          this.$data._pendingProductReq = url

        // cancel current request if running
        if (this.$data._pdtAborter){
          this.$data._pdtAborter.abort()
          // console.debug(`aborting product req`)
        }

        this.$data._pdtAborter = new AbortController()
        this.showLoader(LoaderType.PRODUCTS)

        // this.error = ''

        const res = await fetch(url, {credentials: 'include', signal: this.$data._pdtAborter.signal})

        this.$data._pendingProductReq = '' // clear pending request
        this.$data._pdtAborter = null

        const data = await res.json()

        if (res.status != 200){
          this.raiseError(res.status)
          return false
        }

        if (data && data.p.length === 0){
          this.availabilities = []
          this.raiseError(ErrorCodes.NOPRODUCT_ERROR)
          let trackParams = { av: +this.availPrdOnly, forceloc: +forceLoc}
          if (!forceLoc && !this.location.isEmpty){
            trackParams = Object.assign(trackParams, this.location)
            delete trackParams.auto // whether location is auto is not needed. If kept, it is confusing (it's location location, not the action auto param).
          }
          Tracker.dropTag('nopdt', trackParams)
          return false // finally block is executed before leaving function
        }

        // Set last succesfull request url. Done after checking the response has products.
        // So a request that returned 0 products will be launched again.
        this.$data._lastProductReq = url

        if (data.loc)
          this.useApiLocation(data.loc, forceLoc)

        if (data.tags && this.showProductFilters)
          this.productFilters = data.tags

        let pdts = Mapping.mapProducts(data.p).map(x => Utils.sanitizeProduct(x))

        if (this.masterProductMode){
          // group products and extract masters
          const {masters, groups} = this.getMasterProducts(pdts)
          pdts = masters
          this.productGroups = groups
        }
        this.products = pdts

        if (!data.loc && this.wtbConf.operator.account.tags?.includes('alcohol') && this.country === 'US')
          // raise error if no location is provided for US alcohol products account
          this.raiseError(ErrorCodes.MISSING_REQUEST_LOCATION)
        else
          this.error = ''
        return true
      }
      catch(err){
        // ignore cancelled request error
        if (err.name !== 'AbortError'){
          this.$data._pdtAborter = null
          this.$data._pendingProductReq = '' // clear pending request after an error
          this.raiseError(ErrorCodes.SERVER_ERROR, err)
          Tracker.log('error', {msg: err.message, stack: err.stack, source: 'api-pdt'})
        }
        return false
      }
      finally{
        this.hideLoader(LoaderType.PRODUCTS)

        const tm = performance.getEntriesByName(url)[0]
        if (tm)
          Tracker.log('api-pdt', {pdtReqTime: Math.round(tm.duration)})
      }
    },

    getNextProductPage: function(e){
      console.log('getNextProductPage', e)

      if (this.products.length < this.productTotalCount)
        return this.getPaginatedProducts(this.products.length, null, false, false)
    },

    filtersUpdateHandler: function(payload){
      try{
      this.catalogFilters = payload.filter(x => x[1].length)
                                  .map(x => `"${encodeURIComponent(x[0])}":[${x[1].map(x => '"'+encodeURIComponent(x)+'"').join(',')}]` )
                                  .join(',')
      }
      catch(ex){
        Tracker.log('error', {msg: 'invalid filters provided', stack: ex.stack })
      }
    },

    getPaginatedProducts: async function(offset, limit, forceLoc, ignoreCurrentSelection){

      if (!this.countries.includes(this.country))
        return

      let url
      try{
        if (isNaN(offset))
          offset = 0
        if (isNaN(limit))
          limit = 10

        url = Utils.buildProductsRequestUrl(this.wtbid, this.locale, this.country, this.availPrdOnly, null, this.location, false, this.draft, this.idType, offset, limit, this.catalogFilters, this.tracker)

        // check if running request has the same url. If so, no need to duplicate it.
        // Otherwise keep track of the url
        if (url === this.$data._pendingProductReq || url === this.$data._lastProductReq)
          return
        else
          this.$data._pendingProductReq = url

        // cancel current request if running
        if (this.$data._pdtAborter){
          this.$data._pdtAborter.abort()
          // console.debug(`aborting product req`)
        }

        this.$data._pdtAborter = new AbortController()
        this.showLoader(LoaderType.PRODUCTS)

        const res = await fetch(url, {credentials: 'include', signal: this.$data._pdtAborter.signal})

        this.$data._pendingProductReq = '' // clear pending request
        this.$data._pdtAborter = null

        if (res.status != 200){
          this.raiseError(res.status)
          return
        }

        const data = await res.json()

        // page is empty, just ignore.
        if (data && data.products.length === 0){
          return // finally block is executed before leaving function
        }

        // Set last succesfull request url. Done after checking the response has products.
        // So a request that returned 0 products will be launched again.
        this.$data._lastProductReq = url

        if (data.loc)
          this.useApiLocation(data.loc, forceLoc)

        if (data.tags && this.showProductFilters){
          this.productFilters = data.tags
        }

        const returnedProducts = Mapping.mapProducts(data.products).map(x => Utils.sanitizeProduct(x))
        if (offset === 0){
          this.products = returnedProducts
        }
        else{
          this.products = this.products.concat(returnedProducts)
        }

        this.productTotalCount = data.total

        // if not the first page, selected product is already in list, since we just concatenate results.
        // so no need for a selection update.
        if (offset === 0){
          this.updateProductSelection(ignoreCurrentSelection)
        }
        this.trigger('products-update', {products: Utils.clone(this.products), defaultId: this.product.id})
        this.error = ''
      }
      catch(err){
        // ignore cancelled request error
        if (err.name !== 'AbortError'){
          this.$data._pdtAborter = null
          this.$data._pendingProductReq = '' // clear pending request after an error
          this.raiseError(ErrorCodes.SERVER_ERROR, err)
          Tracker.log('error', {msg: err.message, stack: err.stack, source: 'api-pdt'})
        }
      }
      finally{
        this.hideLoader(LoaderType.PRODUCTS)

        const tm = performance.getEntriesByName(url)[0]
        if (tm)
          Tracker.log('api-pdt', {pdtReqTime: Math.round(tm.duration)})
      }
    },

    // checks if the current selection is in the new product list (unless we force using the default ean/first product).
    // Otherwise take the default ean or the first product.
    // NOTE: product property is always assigned (even if the current selection is found in the list), because that's what triggers
    // the availability request.
    updateProductSelection: function(ignoreCurrentSelection){
      let newPdt

      if (!ignoreCurrentSelection)
        newPdt = this.product.id && this.products.find(x => x.id === this.product.id)

      if (!newPdt && this.defaultEan?.[this.country])
        newPdt = this.products.find(pdt => Utils.isDefaultProduct(pdt, this.defaultEan[this.country], this.idType))

      this.selectProduct(newPdt || this.products[0], true)
    },

    // refresh app location from API response if needed
    useApiLocation: function(location, forceLoc){
      // update location only if current is the default one - or we force server location
      if (forceLoc || this.location.latitude === 0 && this.location.longitude === 0){
        location.auto = true
        this.location = Mapping.mapLocation(location, 'api-pdt')
      }

      if (location.RegionIsMetric != null)
        this.i18n.isMetric = !!location.RegionIsMetric
    },

    // event handler for avails-facet-change event.
    // payload is {key:string, values:string[]}
    updateAvailsFacet: function(payload){
      if (!this.availsFacets)
        this.availsFacets = {}

      let changed = false

      switch (payload.key) {
        case 'class-of-trade':
          this.availsFacets.cots = payload.values
          changed = true
          break
        default:
          break
      }

      // refresh availabilities if any facet changed
      if (changed)
        this.getAvailabilities()
    },

    // retrieve availabilities for a product & location
    getAvailabilities: async function(){
      if (!this.product.ean || !this.countries.includes(this.country)
        // ignore request when location is missing
        || this.error === ErrorCodes.MISSING_REQUEST_LOCATION)
        return

      // abort pending previous request (avails or next)
      if (this.$data._availAborter){
        this.$data._availAborter.abort()
        await new Promise(r => setTimeout(r, 0)) // wait for abort to be effective
      }
      this.$data._availAborter = new AbortController()

      let eans = this.product.ean
      if (this.masterProductMode){
          eans = this.productGroups.get(this.product)?.map(x => x.ean).join(',')
      }

      let url
      try{
        this.showLoader(LoaderType.AVAILABILITIES)

        url = `https://wtb-api-hub.swaven.com/wtb/v2/api/search/${this.isStoreLocator ? 'storelocator/':''}availabilities/${eans}?wtbid=${this.wtbid}&lang=${this.locale}&country=${this.country}&nextEndpointV2=true`

        // dont't include location if it's empty or the API default
        if (!this.location.isEmpty && !this.location.isDefault)
          url += `&lat=${this.location.latitude}&lng=${this.location.longitude}&zc=${this.location.zipCode}&addr=${this.location.address}&cc=${this.location.country}&placetype=${this.location.type}&state=${this.location.region}&city=${this.location.city}`

        if (this.multiOffer) url += '&multioffers=1'
        if (this.draft) url += '&draft=1'
        if (this.availsFacets?.cots?.length > 0) url += `&cots=${this.availsFacets.cots.join(',')}`
        if (this.maxDistance) url += `&maxDst=${this.maxDistance}`
        if (this.dnt) url += '&doNotTrack=true'

        // Encode the base URL with location parameters
        url = encodeURI(url)
        
        // Add Native Experiences querystring parameters, already encoded
        url = this.addNativeExperiencesParams(url)

        const res = await fetch(url, {credentials: 'include', signal: this.$data._availAborter.signal})

        const data = await res.json()

        if (res.status != 200){
          if (res.status === 400 && data.detail === ErrorCodes.MISSING_REQUEST_LOCATION)
            this.raiseError(ErrorCodes.MISSING_REQUEST_LOCATION)
          else
            this.raiseError(res.status)
          return // finally block is executed before leaving
        }

        this.tiedHouseThreshold = data.tiedHouseLaw

        if (data.alcoholSettings){
          this.showRetailerText = !!data.alcoholSettings.showRetailerText

          if (data.alcoholSettings.showLocalRetailers != null)
            this.showLocalRetailers = !!data.alcoholSettings.showLocalRetailers
        }

        // mapping json data to AWE's own model.
        // Provided object is to replace macros in clkUrl.
        let avails = Mapping.mapAvailabilities(data.d, {
          PAGEID: this.tracker.pageId,
          IID: window.name,
          SID: this.tracker.session && this.tracker.session.id,
          SURL: this.tracker.session && this.tracker.session.url,
          SRFR: this.tracker.session && this.tracker.session.referrer,
          RFR: this.tracker.rfr,
          RFR2: this.tracker.rfr2
        })

        if (this.addNearestOnDelivery){
          avails.forEach(x => {
            if (x.store.homeDelivery && x.store.location.latitude == 0 && x.store.location.longitude == 0){
              // if delivery store has no location, reference the first store of same retailer which has a location
              let nearest = avails.find(s => s.retailer.id === x.retailer.id && s.store.location.latitude && s.store.location.longitude)
              if (nearest)
                x.nearestStore = nearest.store
            }
          })
        }

        // multi-offer OFF (default):
        // dedup avails to remove bundles.
        // A single ean is provided, there should not be any offer for other eans.
        if (!this.multiOffer){
          const keys = new Set()
          avails = avails.filter((avail, i) => {
            const key = avail.store.id + avail.product.ean
            const found = keys.has(key)

            if (!found)
              keys.add(key)

            return !found // return first avail for each group
          })
        }

        this.availabilities = avails

        // Make additional requests if any
        if (data.next2){
          this.getOtherRetailers(data.next2)
        }
        else{
          this.$nextTick(() => {
            // trigger event after update, for avail sub-lists to update before sending it.
            this.trigger('availabilities-update', {avails: this.availabilities})

            // Fire tracking - try to get current tab from component, fallback to 'online'
            const currentTab = this.$refs.availsTabView?.selectedTab || 'online'
            const availsForCurrentTab = currentTab === 'online' ? this.onlineAvailabilities : 
                                       currentTab === 'offline' ? this.realAvailabilities : 
                                       currentTab === 'custom' ? (this.$refs.availsTabView?.customAvails || this.availabilities) :
                                       this.onlineAvailabilities // fallback


            Tracker.dropTag('availabilities-update', {
              avails: availsForCurrentTab,
              tab_displayed: currentTab,
              auto: true
            })
          })
        }

        this.lockdown = !!data.lockdown
        this.$data._trkData.avgpc = data.avgp
        this.$data._trkData.avgdst = data.avgdst
        this.$data._trkData.nbres = data.nbres
        this.$data._trkData.nbrefret = data.nbrefret
        this.tracker.availReqId = Utils.uuid()
        Tracker.dropTag('apires')

        if (data.loc && this.location.isEmpty){
          data.loc.auto = true
          this.location = Mapping.mapLocation(data.loc, 'api-avail')
        }

        if (this.availabilities.length === 0 && !data.next2){
          this.raiseError(ErrorCodes.NOSTORE_ERROR)
          return
        }

        if (data.usids && data.usids.length > 0)
          Tracker.dropTag('unavlbsids', {sids: data.usids.slice(0, 20)})

        // keep track of default store, so we can find the store url if alterUrl is not defined
        this.fallbackStoreId = data.dsid

        // show warning for physical stores without geoloc
        let noGeoloc = this.realAvailabilities.filter(x => x.store.location.latitude == 0 && x.store.location.longitude == 0)
                                          .map(x => `\t${x.retailer.name} (${x.retailer.id}) ${x.store.name} (${x.store.id})`)
        if (noGeoloc.length)
          console.info(`These stores have no geolocation data:\n${noGeoloc.join('\n')}`)

        this.error = ''
      }
      catch(err){
        // Aborting a fetch request throws an AbortError, which we just ignore.
        if (err.name !== 'AbortError'){
          this.raiseError(ErrorCodes.SERVER_ERROR, err)
          Tracker.log('error', {msg: err.message, stack: err.stack, source: 'api-avails'})
        }
      }
      finally{
        // hide loader shown on media background click when data not ready
        if (this.isMedia){
          const loader = this.$refs['loader-' + LoaderType.INIT]
          if (loader && loader.visible){
            this.hideLoader(LoaderType.INIT)
            performance.measure('widget-loader-time', 'widget-loader-start')
            Tracker.dropTag('hideWidgetLoader', {duration: Utils.getPerf('widget-loader-time')})
          }
        }

        this.hideLoader(LoaderType.AVAILABILITIES)
        this.availRequestDone = true

        const tm = performance.getEntriesByName(url)[0]
        if (tm)
          Tracker.log('api-avails', {availReqTime: Math.round(tm.duration)})
      }
    },

    // make additional availabilities request for real-time retailers.
    getOtherRetailers: async function(reqId){

      this.showLoader(LoaderType.AVAILS_NEXT, 0)

      try{
        const url=`https://wtb-api-hub.swaven.com/wtb/v2/api/search/availabilities/nextV2/${reqId}`
        // Add Native Experiences parameters to next URLs
        const appendedUrl = this.addNativeExperiencesParams(url)

        const resp = await fetch(appendedUrl, {credentials: 'include', signal: this.$data._availAborter?.signal})
        const response = await resp.json()

        const avails = Mapping.mapAvailabilities(response.d, {
          PAGEID: this.tracker.pageId,
          IID: window.name,
          SID: this.tracker.session && this.tracker.session.id,
          SURL: this.tracker.session && this.tracker.session.url,
          SRFR: this.tracker.session && this.tracker.session.referrer,
          RFR: this.tracker.rfr,
          RFR2: this.tracker.rfr2
        })

        this.availabilities = avails

        // Update tracking data
        this.$data._trkData.nbres = response.nbres
        this.$data._trkData.avgpc = response.avgp
        this.$data._trkData.avgdst = response.avgdst
        this.$data._trkData.nbrefret = response.nbrefret
      }
      catch(ex){
        if (ex.name !== 'AbortError'){
          console.error(ex)
          Tracker.log('error', {msg: ex.message, stack: ex.stack, source: 'api-avails'})
        }
      }
      finally{
        this.hideLoader(LoaderType.AVAILS_NEXT)
      }

      if (this.availabilities.length === 0){
        this.raiseError(ErrorCodes.NOSTORE_ERROR)
      }

      // trigger update after realtime request
      this.$nextTick(() => {
        // trigger event after update, for avail sub-lists to update before sending it.
        this.trigger('availabilities-update', {avails: this.availabilities})

        // Fire tracking - try to get current tab from component, fallback to 'online'
        const currentTab = this.$refs.availsTabView?.selectedTab || 'online'
        const availsForCurrentTab = currentTab === 'online' ? this.onlineAvailabilities : 
                                   currentTab === 'offline' ? this.realAvailabilities : 
                                   this.onlineAvailabilities // fallback

        Tracker.dropTag('availabilities-update', {
          avails: availsForCurrentTab,
          tab_displayed: currentTab,
          auto: true
        })
      })

      return Promise.resolve()
    },

    // Adds Native Experiences parameters to a URL
    addNativeExperiencesParams: function(url){
      const params = new URLSearchParams()
      
      if (window.name) {
        params.set('instanceId', window.name)
      }
      
      if (this.tracker.rfr2) {
        params.set('currentUrl', this.tracker.rfr2)
      }
      
      // Only add sessionUrl if it's different from currentUrl
      if (this.tracker.session?.url && this.tracker.session.url !== this.tracker.rfr2) {
        params.set('sessionUrl', this.tracker.session.url)
      }
      
      const paramsString = params.toString()
      if (!paramsString)
        return url
      
      const separator = url.includes('?') ? '&' : '?'
      return url + separator + paramsString
    },

    // Apply Alcohol restrictions to avails, based on Tied House policy from API
    filterForTiedHouseLaw: function(avails = []) {
      // Filter only if the setting is enabled with a valid value
      if (!this.tiedHouseThreshold)
        return avails

      // Avails can have multiple of the same retailer, so we need to filter down to one each before counting
      const tiedHouseRetailerCount = this._filterForUniqueRetailers(avails, av => av.retailer.isTiedHouseLaw).length

      if (!tiedHouseRetailerCount) return avails

      const isTiedHouseCompliant = tiedHouseRetailerCount >= this.tiedHouseThreshold

      // Remove retailers from avails that are not in compliance with Tied House law
      return avails.filter(({ retailer = {} }) => !retailer.isTiedHouseLaw || isTiedHouseCompliant)
    },

    // @avails: array of availabilities
    // @filterFn: optional filter function to apply to each availability
    _filterForUniqueRetailers: function(avails = [], filterFn) {
      const typos = []
      const filtered = avails.filter(avail => {
        const typo = avail.store.typologyId || avail.retailer.id

        if (!typos.includes(typo) && (!filterFn || filterFn(avail))){
          typos.push(typo)
          return true
        }
        else
          return false
      })
      return filtered
    },

    // filters the product list
    // @products: array of products to filter
    // @field: string, property name to filter on
    // @transform: a function to apply to the field value to get the grouping key
    getMasterProducts: function(products, field = 'name', transform){
      // map of grouping keys and their master product. Used to build the groups map.
      const ids = new Map()

      // map of master products and their variants list (including the master itself)
      const groups = new Map()

      // filter products
      const masters = products.filter(pdt => {
        const key = transform ? transform(pdt[field]) : pdt[field]
        const master = ids.get(key)
        if (master){
          groups.get(master).push(pdt)
        }
        else {
          ids.set(key, pdt)
          groups.set(pdt, [pdt])
        }
        return !master
      })
      return {
        masters,
        groups,
      }
    },

    // on availability click, triggers the desired behavior.
    redirect: function(evt, avail){
      evt.stopPropagation()
      evt.preventDefault()

      const mode = evt.currentTarget.dataset.redirectMode
      switch(mode){
        case 'map':
          window.open(avail.store.gmapUrl)
          Tracker.dropTag('clkloc', {avail: avail, outbound: true, auto: false})
          if (this.isMedia)
            this.tracker.dropClicktag(OUTBOUND_SRC.CLKLOC)
          break
        case 'phone':
          window.open(`tel:${avail.store.phone}`)
          Tracker.dropTag('clkloc', {avail: avail, auto: false, clkloc_type: 'tel'})
          break
        case 'whatsapp':
          window.open(`https://api.whatsapp.com/send?phone=${avail.store.whatsapp}&text=${encodeURIComponent(this.customProps.whatsappMessage)}`)
          Tracker.dropTag('clkloc', {avail: avail, auto: false, clkloc_type: 'whatsapp'})
          break
        case 'appointment':
          window.open(avail.store.appointmentUrl)
          Tracker.dropTag('clkloc', {avail, auto: false, clkloc_type: 'appointment'})
          break
        case 'retailerHome':
          window.open(avail.store.url || avail.retailer.url)
          Tracker.dropTag('clkloc', {avail, auto: false, clkloc_type: 'retailerHome'})
          break
        case 'url':
        default:
          this.buy(avail, evt)
      }
    },

    // updates location, geoloc tracking.
    // called when location is updated outside app, e.g. map or autocomplete components
    setLocation: function(loc){
      this.location = loc
      Tracker.dropTag('geoloc')
    },

    // displays map.
    // avail: (object) availability object to show the popup in map. Optional, no popup shown when missing.
    showMap: function(avail, showRoute = false, auto = false, evt){
      if (evt)
        evt.stopPropagation()
      if (avail && !this.hasPosition(avail))
        return

      this.mapVisible = true

      // check if avail is a new selection. No need to make a clkloc if selected availability does not change.
      const isNewselection = avail && (this.availability == null || this.availability.store.id !== avail.store.id)

      if (avail){
        this.availability = avail
        this.trigger('availability-select', {avail: avail, area: Area.MAIN})
      }

      setTimeout(() => {
        this.$refs.map.show(Utils.clone(avail), showRoute)
      }, 100)

      if (!avail)
        Tracker.dropTag('showmap', {area: Area.MAIN, auto: auto})
      else if (isNewselection)
        Tracker.dropTag('clkloc', {avail: avail, area: Area.MAIN})

      this.trigger('map-show')
    },

    getDirectionsUrl: function(avail) {
      if (!avail || !avail.store?.location) return '#';

      let directionsUrl;

      // Use API-provided directions URL if available
      if (avail.store.directionsUrl) {
        directionsUrl = avail.store.directionsUrl
          .replace('{ORIGIN_LAT}', this.location.latitude || 0)
          .replace('{ORIGIN_LNG}', this.location.longitude || 0);
      } else {
        // Fallaback to manual url building
        directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
          Utils.getStoreFullname(avail)
        )}&origin=${encodeURIComponent(this.location.address)}`;
      }

      return directionsUrl;
    },

    showDirections: function(avail, auto = false) {
      const url = this.getDirectionsUrl(avail);

      window.open(url);
      Tracker.dropTag('clkloc', { avail, auto, clkloc_type: 'googlemaps' })
    },

    closeMap: function(){
      this.mapVisible = false
      this.trigger('map-close')
    },

    // track buy action & redirect to store in new window
    // API-3200: prevent default behavior so that <a> elements do not open new window by default.
    // We do it manually instead, so as to be sure it's done before we generate a new leadid.
    // without that, <a> will open a new window to redirect url as default behavior, but after the new leadid is generated,
    // resulting in tracker in affiliation link having different leadid values for the same event.
    // @avail: availability object
    // @evt: DOM event
    // @trackOnly: boolean, if true, only track the click, do not open the store url.
    // @updateLeadid: boolean, if true, generate a new leadid and update the clkurl.
    buy: function(avail, evt, trackOnly = false, updateLeadid = true){
      evt.stopPropagation()
      evt.preventDefault()
      Tracker.dropTag('buy', {avail: avail, leadid: avail.leadid, outbound: true})

      if (!trackOnly){
        this.trigger('product-buy', {avail: avail, leadid: avail.leadid})
        window.open(avail.clkUrl || avail.store.url || avail.retailer.url)
      }

      if (this.isMedia)
        this.tracker.dropClicktag(OUTBOUND_SRC.BUY)

      // detailed log to find out why some avails are invalid.
      if (!avail){
        let type
        if (avail === null) type = 'null'
        else if (typeof avail === 'undefined') type = 'undefined'
        else if (avail === false) type = 'false'
        else if (avail === 0) type = '0'
        else if (avail === '') type = 'empty string'
        else if (isNaN(avail)) type = 'NaN'

        const attrs = []
        for (let i = 0; i < evt.currentTarget.attributes.length;i++){
          attrs.push({k: evt.currentTarget.attributes[i].name, v: evt.currentTarget.attributes[i].value})
        }
        Tracker.log('error', {
          msg: 'Buy avail is ' + type,
          avails: this.availabilities.map(x => x ? x.store.id : 'null'),
          target: `<${evt.currentTarget.nodeName} ${attrs.map(x => `${x.k}="${x.v}"`).join(' ')}>`
        })
        return
      }

      if (updateLeadid)
        this.getNewLeadid(avail)
    },

    // generate a new leadid and update clkurl for given availability
    getNewLeadid: function(avail){
      if (!avail){
        try{
          throw new Error('avail is undefined')
        }
        catch(ex){
          const trace = ex.stack.substring(0, 512)
          Tracker.log('error', {stack: trace})
        }
        return
      }

      const oldlead = avail.leadid
      avail.leadid =  Utils.uuid().replace(/-/g, '')
      avail.clkUrl = avail.rawClkUrl.replace('{SWN-LEADID}', avail.leadid)

      // map component gets a clone of the selected availability.
      // If the clicked availability is the one selected in map, new leadid & clkurl will not be reflected.
      // In that case, clone the map-selected avail and update its leadid & clkurl,
      // then replace the object with the updated clone.
      if (this.$refs.map && !this.$refs.map.selected?.isDefault && avail !== this.$refs.map.selected){
        const mapAvail = Utils.clone(this.$refs.map.selected)
        mapAvail.leadid = avail.leadid
        mapAvail.clkUrl = avail.clkUrl
        this.$refs.map.selected = mapAvail
      }
      // if the clicked availability is the map-selected one (e.g. from map infowindow),
      // it can be the one passed to map.show, or another one.
      // If the latter, it's the same object, so leadid is already updated.
      // If the former, the availability is cloned on passing it to map.show,
      // so we need to find the original one (from leadid, if avail has a nearest store, 'store' property is replaced in map-selected avail so we can't use store id),
      // and update the avail in array with the new leadid and clkurl.
      // This triggers updates for availabilities list and other lists the avail may be part of (e.g. onlineAvailabilities or realAvailabilities)
      else if (avail === this.$refs.map?.selected && avail !== this.availability){
        const appAvail = this.availabilities.find(x => x.leadid === oldlead)
        appAvail.leadid = avail.leadid
        appAvail.clkUrl = avail.clkUrl
      }
    },

    // Assigns provided product as current selection, and drops clk tracking.
    selectProduct: function(pdt, auto = false){
      const change = this.product?.id !== pdt?.id
      const isImageChanged = this.product?.imgUrl !== pdt?.imgUrl
      const oldProductEan = this.product?.ean
      
      if (!pdt)
        this.product = {
          name:'',
          name2:'',
          pkg:'',
          description:'',
          ratings: {}
        };
      else
        this.product = pdt

      if (change) {
        Tracker.dropTag('clk', {auto: auto, autoSel: this.eans.length === 1 && !this.isRange})

        // If widget has already opened once and the displayed image changes:
        //   - Check whether the change is only between variants of the same product - grouping prevents image re-fetch.
        //   - If it’s a different product altogether, trigger the image loader to show a loading state.
        if (this.firstOpeningDone && isImageChanged) {
          const isSameVariant = this.productVariants && this.productVariants.some(variant => variant.ean === oldProductEan);
          
          // Show loader if the product is changed
          if (!isSameVariant) 
            this.showImageLoader()
        }
      }

      if (!isImageChanged) {
        // Hide loader if the product image is not changed
        this.hideImageLoader()
      }
    },

    setEans: function(updates, auto = false){
      const {eans, defaultEan} = updates

      if (!this.allEans)
        this.allEans = { [this.country]: eans }
      else{
        this.allEans = {
          ...this.allEans,
          [this.country]: eans
        }
      }

      let defaultEanUpdate = false

      if (defaultEan){
        // check the default ean has changed
        defaultEanUpdate = this.defaultEan?.[this.country] !== defaultEan
        if (this.defaultEan)
          this.defaultEan[this.country] = defaultEan
        else
          this.defaultEan = { [this.country]: defaultEan }
      }

      this.getProducts(this.eans, false, true, defaultEanUpdate)
    },

    // Hide loading spinner for product images when no change is detected
    hideImageLoader: function() {
      const nds = document.querySelectorAll('.wl');
      for (const nd of nds) {
        const img = nd.querySelector('img');
        if (!img) continue;
        nd.classList.remove('loading');
      }
    },

    // Show loading spinner for product images when switching products
    showImageLoader: function() {
      const nds = document.querySelectorAll('.wl');

      for (const nd of nds) {
        const img = nd.querySelector('img');
        if (!img) continue;

        // Add loading class immediately
        nd.classList.add('loading');

        // Set up handlers for both successful loads and errors
        img.addEventListener('load', Utils.removeImgLoadingHandler);
        img.addEventListener('error', Utils.removeImgLoadingHandler);
      }
    },

    getDirections: function(e){
      this.$refs.map.getDirections()
    },

    // triggers html5 geoloc, with fallback to server-provided
    autoloc: function(e){
      const auto = !e // false if event exists (user action call). true if called programmatically without arg, e.g. first opening event handler
      hostCom.send('awe:getLocation', {auto: auto}, async (data) => {
        if (data.lng && data.lat){
          const loc = await Utils.getReverseGeocoding(data.lng, data.lat, [], this.language, 'html5', auto)
          if (loc)
            this.location = loc
          else
            await this.getProducts(this.eans, true)
        }
        else // html5 geoloc failure (e.g. denied or unavailable), make a products request to get its from server
          await this.getProducts(this.eans, true)

        if (!auto) // track only if argument if user-made
          Tracker.dropTag('geoloc')
      })
    },

    getTrkData: function(){
      return this.$data._trkData
    },

    getStoreIds: function(){
      return this.availabilities.slice(0, 20).map(x => x.store.id)
    },

    // Helper function to get the appropriate availabilities and tab for tracking
    // Returns the availabilities that should be shown by default and the corresponding tab name
    getDefaultTabAvailabilities: function(){
      // Get the default tab from the component, fallback to 'online'
      const configuredTab = this.$refs.availsTabView?.defaultTab || 'online'
      
      // Get availabilities for the configured default tab
      let availsForTab
      switch (configuredTab) {
        case 'online':
          availsForTab = this.onlineAvailabilities
          break
        case 'offline':
          availsForTab = this.realAvailabilities
          break
        case 'custom':
          // Custom availabilities would need to be accessible here - fallback to global list
          availsForTab = this.availabilities
          break
        default:
          availsForTab = this.availabilities
      }
      
      // If the configured tab has no content, fall back to any available content
      if (!availsForTab || availsForTab.length === 0) {
        if (this.onlineAvailabilities && this.onlineAvailabilities.length > 0) {
          return {
            avails: this.onlineAvailabilities,
            tab_displayed: 'online'
          }
        } else if (this.realAvailabilities && this.realAvailabilities.length > 0) {
          return {
            avails: this.realAvailabilities,
            tab_displayed: 'offline'
          }
        } else {
          // Ultimate fallback - use global list
          return {
            avails: this.availabilities,
            tab_displayed: configuredTab
          }
        }
      }
      
      return {
        avails: availsForTab,
        tab_displayed: configuredTab
      }
    },

    // returns cloned location object
    getLocation: function(){
      return Utils.clone(this.location)
    },

    // track event & send close command to host
    closeWidget: function(e = false){
      if (e.repeat) return

      let auto = !!e

      // if triggered by a DOM event, cannot be automatic
      if (e instanceof Event){
        auto = false
        e.stopPropagation()
      }

      Tracker.dropTag('close', {auto: auto})
      hostCom.send('awe:close')
    },

    // sends a custom event. Might be called before the app is mounted in DOM. In such case, the event is discarded.
    // type (string): event type
    // details (object): event details
    trigger: function(type, details = {}){
      let event = new CustomEvent(type, { detail: details, bubbles: true })

      if (this.$el)
        this.$el.dispatchEvent(event)
    },

    // proxy method to easily add events on the app's container node
    addEventListener: function(type, listener, options = false){
      this.$el.addEventListener(type, listener, options)
    },

    // proxy method to easily remove events on the app's container node
    removeEventListener: function(type, listener, options = false){
      this.$el.removeEventListener(type, listener, options)
    },

    // checks if the iframe height must be updated
    checkHeight: async function(){
      // this is supposed to wait until the DOM has updated (https://vuejs.org/v2/api/#Vue-nextTick)
      // but even at that time the view is not fully rendered (e.g. images being downloaded). So we do a few checks
      // at regular intervals for some time to make sure we have the correct height
      await this.$nextTick()

      const CHECK_INTERVAL = 100,
            CHECK_DURATION = 1000

      let start = null,
          last = null // timestamp of last check performed

      const check = (ts) => {
        if (!start)
          start = last = ts

        // don't check at every frame refresh, every 100ms is enough
        if (ts - last >= CHECK_INTERVAL){
          const height = Math.ceil(window['app-ctnr'].getBoundingClientRect().height)
          // console.log(`height: ${height}\tcurrent: ${this.$data._currentHeight}`)

          if (height != this.$data._currentHeight){
            hostCom.send('awe:resize', {height: height})
            this.$data._currentHeight = height
          }
          last = ts
        }

        // stop after 1000ms, otherwise run next step
        if (ts - start < CHECK_DURATION)
          this._rafid = window.requestAnimationFrame(check)
        else{
          window.cancelAnimationFrame(this._rafid)
        }
      }

      window.cancelAnimationFrame(this._rafid) // cancel potential previous animation
      this._rafid = window.requestAnimationFrame(check)
    },

    // adjust iframe height to content when window is resized
    resizeWindow: function(){
      if (this.$data._resizeTimout != null) return //debounce

      this.$data._resizeTimout = setTimeout(() => {
        this.checkHeight()
        this.$data._resizeTimout = null
      }, 100)
    },

    // shows error with the appropriate error message
    raiseError: function(error, data){
      // if error is a number, it is interpreted as an HTTP status code
      if (typeof error === 'number'){
        switch(error){
          case 401:
          case 403:
            error = ErrorCodes.AUTH_ERROR
            break
          case 402:
            error = ErrorCodes.DISABLED_ERROR
            break
          case 500:
          default:
            error = ErrorCodes.SERVER_ERROR
        }
      }

      const hardErrors = [
        ErrorCodes.AUTH_ERROR,
        ErrorCodes.DISABLED_ERROR,
        ErrorCodes.INVALID_TOUCHPOINT_ERROR,
        ErrorCodes.NO_EAN_ERROR,
        ErrorCodes.SERVER_ERROR
      ],
      // determines if error is a hard or soft one
      isHardError = hardErrors.includes(error)
                    // product config in monocountry
                    || (error === ErrorCodes.PRODUCT_CONFIG_ERROR && !this.wtbConf.multiCountry)

      if (isHardError){
        this.availabilities = []
        this.$refs.errMon.show(error) // error component is always present, added by preprocessor if not in template.
      }
      else
        this.error = error

      this.trigger('error', {msg: error, err: data})

      let msg = `${error}: ${ErrorLabels[error]}`
      if (data){
        if (data.message)
          msg += '\n' + data.message
        if (data.stack)
          msg += '\n' + data.stack
      }
      console.error(msg)
    },

    showLoader: function(type, delay){
      var ref = 'loader-' + type,
          loader = this.$refs[ref] || this.$refs.loader

      if (loader)
        loader.show(delay)

      this.trigger('loader-show', {type, delay})
    },

    hideLoader: function(type){
      var ref = 'loader-' + type,
          loader = this.$refs[ref] || this.$refs.loader

      if (loader)
        loader.hide()

      this.trigger('loader-hide', {type})
    },

    // Returns whether an availability has a position, be it the actual store or a linked store oft the same retailer
    hasPosition: function(a){
      // nearest store is added only if it has latitude & longitude, no need to check that here
      return !!(a && (a.store.location.latitude && a.store.location.longitude || a.nearestStore))
    },

    // checks whether a template node must be rendered.
    // If init params provide layout (from shoptag), use that to check if template must be rendered.
    // Otherwise, the parent iframe size must be within the boundaries defined for each template, or corresponds to predefined dimensions (old fixed size ads)
    // that may be outside boundaries but are mapped to that layout.
    // This method is run for each subtemplate at each update. To speed things up, result for a set of arguments is stored and reused.
    /*selectTemplate: function(minWidth, maxWidth, minHeight, maxHeight, name){
      if (!this.initDone) return false // no need to run the method before init completion

      // use layout name if provided
      if (this.layoutName){
        // console.log(`find by layoutname ${this.layoutName} / ${name}: ${name === this.layoutName}` )
        const show = this.layoutName === name
        return show
      }

      // old fixed size ads are mapped to a layout. Layout is defined by min/max dimensions (object key), fixed sizes as values
      const fixedSizeMapping = {
        '900x225_1800x450': [[970, 250], [1000, 300]],
        '300x50_450x75': [[300, 50], [320, 50]],
        '600x75_1200x150': [[728, 90]],
        '900x90_1800x180': [[970, 90]],
        '300x900_450x1350': [[300, 1050]],
        '160x640_240x960': [[120, 600], [160, 600]],
        '300x300_450x450': [[300, 250]],
        '200x100_300x150': [[120, 60]]
      }

      // Checks dimensions to determine which template to use

      // get dimensions, or return false if not available yet
      let width, height
      if (this.sizeMode === 'fixed'){
        width = parseInt(this.$el.style.width)
        height = parseInt(this.$el.style.height)
      }
      else if (this.$data._iframeSize.width !== -1){
        width = this.$data._iframeSize.width
        height = this.$data._iframeSize.height
      }
      else{
        // console.log(`${minWidth}.${maxWidth}.${minHeight}.${maxHeight}.${name} - No dimensions`)
        return false
      }

      const hash = `${minWidth}.${maxWidth}.${minHeight}.${maxHeight}.${name}_${width}.${height}`

      // use known value if it exists
      if (this.$data._templateSelectionResults[hash] != null){
        // console.log(hash + ' - found: ' + this.$data._templateSelectionResults[hash])
        return this.$data._templateSelectionResults[hash]
      }
      // console.log(hash)

      let show = true
      if (minWidth && maxWidth){
        if (minWidth > width || width > maxWidth)
          show = false
      }
      if (show && minHeight && maxHeight){
        if (minHeight > height || height > maxHeight)
          show = false
      }

      if (!show){
        const key = `${minWidth}x${minHeight}_${maxWidth}x${maxHeight}`
        if (fixedSizeMapping[key] && fixedSizeMapping[key].some(x => x[0] === width && x[1] === height))
          show = true
      }

      // template name is added to the root element as a class
      if (name && show){
        this.layoutName = name
      }

      this.$data._templateSelectionResults[hash] = show // save results
      return show
    },*/

    /*
    // Determines url to use when user clicks on the background (not on a store)
    redirectBackgroundClick: function(){
      let url, source
      if (this.alterUrl){
        url = this.alterUrl
        source = OUTBOUND_SRC.CUSTOM
        Tracker.dropTag('custredirect', {auto: true, outbound: false})
      }
      else if (this.fallbackStoreId){
        let defaultAvail = this.availabilities.find(x => x.store.id === this.fallbackStoreId)
        if (defaultAvail && defaultAvail.clkUrl){
          url = defaultAvail.clkUrl
          Tracker.dropTag('buy', { avail: defaultAvail, leadid: defaultAvail.leadid, auto: true})
          this.trigger('product-buy', {avail: defaultAvail, leadid: defaultAvail.leadid})
          this.getNewLeadid(defaultAvail)
          source = OUTBOUND_SRC.BUY
        }
      }

      if (url){
        this.tracker.dropClicktag(source)
        Tracker.dropTag('clkout', {outbound: true})
        window.open(url)
      }
      else{
        // if url is not set, that means alterUrl and fallbackStoreId are not defined,
        // meaning the availability request is not done and the widget is not fully ready.
        // API-3233: show loader. if already shown, clicktag fallbackUrl
        const loader = this.$refs['loader-' + LoaderType.INIT]

        if (!loader) // Should not happen !
          return Tracker.log('error', {msg: 'Loader not found'})

        if (!loader.visible){
          performance.mark('widget-loader-start')
          this.showLoader(LoaderType.INIT, 0)
          Tracker.dropTag('showWidgetLoader')
        }
        else if (this.wtbConf.fallbackurl){
          this.tracker.dropClicktag(OUTBOUND_SRC.FALLBACK)
          Tracker.dropTag('fallback', {outbound: true})
          window.open(this.wtbConf.fallbackurl)
        }
        else{ // should not happen
          Tracker.log('error', {msg: 'fallbackurl is not defined.'})
        }
      }
    },*/

    /*
    // Uses an image as background for the widget.
    // If link is an absolute url, we can use it directly.
    // If it is relative, the context is the widget's parent window.
    setBackgroundImage: function(imageUrl, referrer){
      var url

      if (imageUrl.startsWith('http') || imageUrl.startsWith('//'))
        url = imageUrl
      else if (referrer)
        url = new URL(imageUrl, referrer) // resolves a relative url from the parent window's location
      else
        // if document.referrer is empty, that's because of security concerns (e.g.the host page uses file: protocol).
        // In that case we wouldn't be able to load the resource anyway, so just forget it.
        console.log(`Warning: Referrer was not provided, cannot compute relative image url. If you are on file: protocol, the image wouldn't load anyway.`)

      if (url){
        this.$el.style['background-image'] = `url(${url})`
        this.$el.style['background-size'] = '100% 100%'
      }
    },*/

    setAvailability: function(e){
      this.availability = e.avail
      this.trigger('availability-select', {avail: this.availability})
    },

    // whether given availability has schema-structured opening hours
    hasOpeningTimes: function(avail){
      const store = avail && (avail.nearestStore || avail.store)
      if (!store)
        return false

      return store.openingType === 3 && store.openingHours
    },

    getIframeSize: function(useContainer = false){
      return new Promise(resolve => {
        hostCom.send('awe:getSize', {useContainer: useContainer}, size => {
          this.$data._iframeSize = { width: size.width, height: size.height }
          resolve()
        })
      })
    },

    // define app root element size with inline style
    setRootSize: function(dimensions){
      this.$el.style.width = dimensions.width + 'px'
      this.$el.style.height = dimensions.height + 'px'
    },

    // injects a style node with rules from remote conf that are applied
    // using css custom properties
    injectCustomStyle: function(params) {
      this.injectLegacyCustomStyle(params)

      // build objects needed to create stylesheet content
      const {rules, fontImports, fontFaces} = this._buildCustomStyle(params)

      if (rules.size > 0 || params['custom-css'] || fontImports.length > 0 || fontFaces.length > 0){

        const value = this._serializeCustomStyle(rules, fontImports, fontFaces, params['custom-css'])

        const existingNd = document.getElementById('custom-style')
        if (existingNd)
          existingNd.innerHTML = value
        else{
          const nd = document.createElement('style')
          nd.innerHTML = value
          nd.id = 'custom-style'
          document.body.appendChild(nd)
        }
      }
    },

    // reads the remoteConfig params and builds objects for serialization
    // @params: (object) remoteConfiguration parameters
    _buildCustomStyle(params){

      // map of string (css selector) to array of strings (css directives)
      const rules = new Map()
      // list of @import rules for custom fonts
      const fontImports = []
      // list of @font-face declarations for custom fonts
      const fontFaces = []

      // loop over params and set css accordingly
      Object.entries(params).forEach(([option, value]) => {
        if (!this.customStyleMapping[option]) return
        if (value === '' || value == null) return

        let fontRule = false
        let prop, ctnr
        [prop, ctnr] = this.customStyleMapping[option]
        ctnr = ctnr || '#app-ctnr'

        if (option.includes('color')){
          // color may be missing prepending #, add it
          if (/^([0-9a-fA-F]{3}){1,2}$/.test(value))
            value = '#' + value
          // or it may be custom property name, and needs var() around it.
          else if (value.startsWith('--'))
            value = `var(${value})`
        }
        else if (option === 'background-image'){
          // if url (protocol is mandatory, '//example.com' is not allowed anymore),
          // check wether video or image.
          if (value.startsWith('http')){
            if (/\.(webm|mp4)$/i.test(value)){
              this.$data._backgroundVideo = value
              return
            }
            else{
              this.$data._backgroundVideo = null
              value = `url("${value}")`
            }
          }
          // if value is not a gradient definition, then it must be a color.
          // In that case we assign the correct property, not backgroud-image.
          else if (!value.includes('gradient')){
            prop = '--main-bg'
          }
        }
        else if (option.startsWith('font-')){
          fontRule = true // font rules are handled separately,  do not add to standard rule object
          Utils.parseFont(option, value, fontImports, fontFaces)
        }

        if (!fontRule) {
          const directive = `${prop}:${value};`
          const block = rules.get(ctnr)

          if (block)
            block.push(directive)
          else
            rules.set(ctnr, [directive])
        }
      })

      // 'custom-font' does not appear in template style-mapping lists and is thus ignored
      // from the above loop, so we handle it separately.
      if (params['custom-font']){
        Utils.parseFont('font-default', params['custom-font'], fontImports, fontFaces)
      }

      return {rules, fontImports, fontFaces}
    },

    // Creates and returns a stylesheet content from rules, imports and font-faces declarations
    _serializeCustomStyle(rules, fontImports, fontFaces, customCss){
      let value = '' // css content to inject
      let fontProperties = '' // css variables declarations for custom fonts

      // @import rules must be at the top of style node.
      // can be either from a custom font css url or from custom css.
      fontImports.forEach(x => {
        value += x.rule + '\n'
        fontProperties += Utils.setFontVariable(x.optionName, x.fontName)
      })

      // custom css can include import rules that also must be before everything else
      if (customCss)
        value += customCss

      // declare all font-face directives
      fontFaces.forEach(x => {
        value += x.rule + '\n'
        fontProperties += Utils.setFontVariable(x.optionName, x.fontName)
      })

      // set custom variables with font names
      if (fontProperties){
        value += `#app-ctnr{ ${fontProperties} }`
      }

      // add all rules
      for (const [selector, directives] of rules){
        value += `${selector}{ ${directives.join('')} }\n`
      }

      return value
    },

    // injects style defined by the remote conf for rules defined the old-school way
    // (as in lp legacy template)
    injectLegacyCustomStyle: function(params) {
      if (Object.keys(app.legacyCustomStyle).length === 0) return // if no custom css defined in the template, just leave

      const style = {},
            regexSplit = /'[^']+?'([^\s]?)+|\S+/g // regex to find word or quoted string with/without a slashed specific value

      // loop all style keys defined in template
      Object.keys(app.legacyCustomStyle).forEach(elem => {
        if (!params[elem]) return // property is not used by current wtbConf, nothing to do

        // split line into a list of selectors (w/ optional css prop) and process each one
        app.legacyCustomStyle[elem].match(regexSplit).forEach(value => {
          if (!value) return

          let [selector, cssProperty] = value.split('/'),
              propValue = params[elem]

          selector = selector.replace(/'/g, '')
          cssProperty = cssProperty ? cssProperty.replace(/'/g, '') : app.legacyCustomStyleValues[elem] // use default property if not specified for this selector

          if (!style[selector])
            style[selector] = ''

          if (cssProperty.includes('color') || cssProperty === 'background')
            propValue = Utils.prependColor(propValue)

          if (cssProperty === 'font-src'){
            style['@font-face'] = `font-family: '${params["global-font-family"]}'; src:url('${propValue}');`
          }
          else
            style[selector] += `\t${cssProperty}: ${propValue} !important;\n`
        })
      })

      // create style content from
      if (Object.keys(style).length){
        let innerStyle = Object.entries(style).map(([sel, props]) => `${sel}{${props}}`).join('\n'),
              ifrStyleNd = document.createElement('style')

        ifrStyleNd.innerHTML = innerStyle
        ifrStyleNd.id = 'legacy-custom-style'
        // inject in body rather than head, so that it comes after default style and takes priority.
        document.body.appendChild(ifrStyleNd)
      }
    },

    // Wrapper over tracker dropTag method
    dropTag: function(evt, action, ...args){
      evt.stopPropagation()

      if (action ==='clicktag' && this.isMedia){
          this.tracker.dropClicktag(args && args[0] || OUTBOUND_SRC.CUSTOM)
          return
      }

      const params = {}

      switch(action){
        case 'clkloc':
          params.auto = !!args[1]?.auto
          params.outbound = !!args[1]?.outbound
          params.clkloc_type = args[1]?.clkloc_type || null
        case 'appointment':
          params.avail = args[0]
          params.area = Area.MAIN
          break
        case 'clknav':
        case 'mover':
          params.area = Area.MAIN
          break
        case 'custredirect':
          // for custredirect, outbound defaults to true
          params.auto = args[0] ? !!args[0].auto : false
          params.outbound = args[0] && args[0].outbound != null ? !!args[0].outbound : true
          break
        default:
          params.unknown = true
      }

      if (params.unknown) return // ignore unknown tracking actions

      Tracker.dropTag(action, params)
    },

    // start IntersectionObserver to track visible stores
    trackVisibleStores: function(){
      const VISIBILITY_THRESHOLD = .5
      if (typeof IntersectionObserver === 'undefined') return // not available in IE11

      if (this._intersectObserver){
        this._intersectObserver.disconnect() // stops listening to current nodes
      }
      else{
        this._intersectObserver = new IntersectionObserver(this.visibleStoreHandler, {
          root: null, // root node is viewport
          rootMargin: '0px',
          threshold: VISIBILITY_THRESHOLD // percentage of element area that must be in viewport for being considered visible
        })
      }

      // get all availability nodes and observe them
      const nds = document.getElementsByClassName('_avail')
      for (let i = 0; i < nds.length; i++){
        this._intersectObserver.observe(nds[i])
      }
      Tracker.log('observeStores', {nodesCount: nds.length})

      setTimeout(this._checkSeenStores, 200)

      // clear previous interval function, if any
      clearInterval(this.$data._availTrackItv)

      // use interval to look if there are visible stores to track
      this.$data._availTrackItv = setInterval(this._checkSeenStores, 1500)


      // Alternative detection method
      // for each availability, get its position and compute if its in the viewport.
      const storemap = new WeakMap()
      setInterval(() => {
        const vh = document.documentElement.clientHeight,
              vw = document.documentElement.clientWidth

        const sids = []
        for (let i = 0; i < nds.length; i++){
          const nd = nds[i],
                dims = nd.getBoundingClientRect()
          const area = dims.width * dims.height
          let hiddenPortion = 0

          // compute surface area outside of the viewport
          // BUG: if 2 adjacent sides are outside viewport, the computed hidden area
          // is bigger than what it really is.
          if (dims.top < 0)
            hiddenPortion += Math.abs(dims.top) * dims.width
          if (dims.bottom > vh)
            hiddenPortion += (dims.bottom - vh) * dims.width
          if (dims.left < 0)
            hiddenPortion += Math.abs(dims.left) * dims.height
          if (dims.right > vw)
            hiddenPortion += (dims.right - vw) * dims.height
          const visible = hiddenPortion / area <= (1 - VISIBILITY_THRESHOLD)

          if (visible && !storemap.has(nd)){
            storemap.set(nd, true)
            sids.push(nd.dataset.sid)
          }
        }
        if (sids.length)
          Tracker.dropTag('altStoreDisplayed', {sids: sids.slice(0, 20)})
          // console.log('track: ' + sids.join(','))
      }, 1000)
    },

    _checkSeenStores: function(){
      const newSids = []

      // find newly visible avails, track them.remove them from set.
      // Since we unobserve them as soon as they're seen,
      // they won't show up again, no risk of double tracking.
      for (const sid of this.$data._trackedAvails){
        newSids.push(sid)
        this.$data._trackedAvails.delete(sid)
      }

      if (newSids.length > 0)
        Tracker.dropTag('storeDisplayed', {sids: newSids.slice(0, 20)})
    },

    // IntersectionObserver handler
    visibleStoreHandler: function(entries, observer){
      entries.forEach(entry => {
        // an entry is sometimes included even if its ratio is below the threshold, so we check that explicitely
        if (entry.isIntersecting && entry.intersectionRatio >= observer.thresholds[0]){
          observer.unobserve(entry.target) // no need to further observe an availability once it's been tracked
          this.$data._trackedAvails.add(entry.target.dataset.sid)
        }
      })
    },

    updateLocale: function(lang, oldLang, base, oldBase){
      const add = [], remove = []
      if (lang !== oldLang){
        add.push(lang)
        if (base !== lang)
          add.push(base)

        if (oldLang && !add.includes(oldLang))
          remove.push(oldLang)
        if (oldBase && !add.includes(oldBase) && !remove.includes(oldBase))
          remove.push(oldBase)
      }

      if (add.length)
        hostCom.send('awe:updateLocale', {add: add, remove: remove})

      return {add: add, remove: remove} // not used by app but in unit tests
    },

    closeLockdownNotif: function(){
      this.lockdownNotifClosed = true
    },

    // empties location address. This only impacts the 'location' property, which is a computed one.
    // It does not affect the actual _location object.
    clearAddress: function(){
      this.location.address = ''
    },

    // when safeframe are detected via blocked postmessage, set it and set the source
    setSafeFrame: function(){
      this.withSafeFrame = true
      this.tracker.safeFrameDetector = 'postmessage'
    },

    setDefaultAddress: async function(){
      try{
        const feats = await Utils.queryMapbox(this.defaultCountry, [this.defaultCountry], this.locale, 1)
        if (feats && feats[0]){
          const loc = Utils.parseFeature(feats[0])
          // bypass setting logic. No need to refresh anything, nor save it in session storage.
          this.$data._location = loc
        }
      }
      catch(ex){
        console.error('setDefaultAddress error')
        console.error(ex)
      }
    },

    // Sets up resize observer on app-ctnr.
    monitorHeight: function(){
      if (window.ResizeObserver){
        const obs = new ResizeObserver(([entry]) => {
          const height = entry?.borderBoxSize?.[0]?.blockSize || entry?.contentRect?.height

          if (height !== this.$data._currentHeight){
            hostCom.send('awe:resize', {height: height})
            this.$data._currentHeight = height
          }
        })
        obs.observe(document.getElementById('app-ctnr'))
      }
      else
        // If not supported, fall back to old-fashioned resize event listener.
        // Even if it does not trigger in some cases (e.g. change max-height in css)
        window.addEventListener('resize', this.resizeWindow)
    },

    // process image origin url
    cleanOriginUrl: function(url){
      if (!url)
        return ''

      // replace content.static-swaven host with a non-caching one
      // add protocol if missing
      // encode for inclusion in cloudImage path
      url = url.replace(/^(https?:)?\/\/content\.static-swaven\.com/, 'https://content-nocache.static-swaven.com')
                .replace(/^\/\//, 'https://') // use https if protocol is missing
      return encodeURIComponent(url)
    },

    // Injects glassbox script in the iframe
    injectGlassbox: function(sessionId, reportURI, glassboxPath){

      // update iframe url with sessionId. Uses history API to avoid a reload.
      const qsParam = `${window.location.href.includes('?') ? '&': '?'}_cls_s=${sessionId}`
      history.pushState(null, '', window.location.href + qsParam)

      const d = document.createElement('script')
      d.id = '_cls_detector'
      d.setAttribute('data-clsconfig', `reportURI=${reportURI}`)
      const GLASSBOX_PATH = glassboxPath
      d.src = `https://cdn.gbqofs.com/${GLASSBOX_PATH}/p/detector-dom.min.js` // value contains the placeholder for GLASSBOX_PATH.
      document.body.appendChild(d)
    },
  }
}

// stub for browsers that use the standard build but do not support AbortController.
// Request cancelling will not work with this but nevermind, it's only ~0.5% of widget loads. At least it won't throw an error.
if (typeof window.AbortController === 'undefined'){
  window.AbortController = function(){
    return {
      signal: null,
      abort: function(){return}
    }
  }
}
